/**
 * Normalise a provisioning-task error into text an operator can read.
 *
 * WHY THIS EXISTS — the type lies, and it lied in two places at once.
 * `@lexiun/org-admin`'s `TaskStep.error` is declared `string`, but the wire
 * sends an object (`{code, message}`-shaped, and sometimes a whole Azure error
 * body). `TaskRecord.error` is honest about it (`string | Record<…>`), so every
 * site that touched the TASK error got it right and every site that touched the
 * STEP error got it wrong:
 *
 *   - `CreateOrgWizard.describeTaskFailure` interpolated it → "[object Object]"
 *   - `Tasks.tsx` rendered it as a React child → **Minified React error #31**,
 *     which took the whole page to "Something went wrong"
 *
 * Both were found in prod (2026-08-12) while diagnosing a provisioning blocker,
 * where the operator had to read the failure out of the network response because
 * the UI that exists to show it had crashed.
 *
 * ⇒ Never render a task/step error directly. Route it through here.
 *
 * The contract: this ALWAYS returns readable text or `undefined`. It never
 * returns an object, and it never silently drops information — an unreadable
 * shape is stringified rather than swallowed, because "we could not read it" and
 * "there was nothing" must not look the same. [[unknown-must-be-representable]]
 */
export function errorText(value: unknown): string | undefined {
  if (value == null) return undefined;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  }

  if (typeof value !== "object") return String(value);

  // The common server shape: {code, message}. Prefer the human half, but keep
  // the code — it is what a runbook greps for.
  const rec = value as Record<string, unknown>;
  const message = typeof rec.message === "string" ? rec.message.trim() : undefined;
  const code = typeof rec.code === "string" ? rec.code.trim() : undefined;

  if (message && code) return `${code}: ${message}`;
  if (message) return message;

  try {
    return JSON.stringify(value);
  } catch {
    // Circular or otherwise unserialisable — say so rather than returning
    // nothing, which would read as "no error detail".
    return "[unreadable error object]";
  }
}
